/**
 * Starting the native (Landi) scanner from the page. Same API in the dark POS and the handheld apps.
 *
 * The Android wrapper exposes on window.AndroidPOS (the iOS app exposes the same methods on window.NativePOS, backed by
 * the device's camera): isScannerAvailable(), startScan(optionsJson), stopScan(); and calls back
 * window.onScanState(jsonString) with {state: 'idle'|'scanning'|'timeout'|'cancelled'|'error', message?, error?}. A scanned code itself arrives through window.onBarcodeScanned,
 * which the ScanListener already routes, so this module only STARTS and STOPS a scan and reports the state.
 *
 * Everything here is guarded and never throws: in a browser or an older app the scanner is simply "not ready".
 */

const bridge = () => (typeof window !== 'undefined' ? window.AndroidPOS || window.NativePOS : undefined);

/** True only when the wrapper has startScan AND isScannerAvailable() says true. */
export function scannerReady() {
  try {
    const b = bridge();
    if (!b || typeof b.startScan !== 'function') return false;
    if (typeof b.isScannerAvailable !== 'function') return false;
    return b.isScannerAvailable() === true;
  } catch (e) {
    return false;
  }
}

export const DEFAULT_SCAN_OPTIONS = Object.freeze({ timeoutMs: 10000, continuous: false });

/** Asks the wrapper to start one scan. Returns true when the call was made. */
export function startScanSession(options = DEFAULT_SCAN_OPTIONS) {
  try {
    if (!scannerReady()) return false;
    bridge().startScan(JSON.stringify({ ...DEFAULT_SCAN_OPTIONS, ...options }));
    return true;
  } catch (e) {
    return false;
  }
}

export function stopScanSession() {
  try {
    const b = bridge();
    if (!b || typeof b.stopScan !== 'function') return false;
    b.stopScan();
    return true;
  } catch (e) {
    return false;
  }
}

/** window.onScanState payload (a JSON string, or already an object) -> { state, message?, error? }. */
export function parseScanState(payload) {
  try {
    const v = typeof payload === 'string' ? JSON.parse(payload) : payload;
    if (v && typeof v === 'object' && typeof v.state === 'string') {
      const out = { state: v.state };
      if (typeof v.message === 'string') out.message = v.message;
      if (v.error !== undefined && v.error !== null) out.error = v.error;
      return out;
    }
  } catch (e) {
    /* fall through */
  }
  return { state: 'error', message: 'Unreadable scanner state' };
}

// A tiny emitter so many buttons can listen while window.onScanState is installed exactly once,
// chained after whatever handler was there (never overwriting another subscriber).
const listeners = new Set();
let installed = false;

function install() {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  const previous = window.onScanState;
  window.onScanState = (payload) => {
    if (typeof previous === 'function') {
      try {
        previous(payload);
      } catch (e) {
        /* another subscriber's problem */
      }
    }
    const state = parseScanState(payload);
    listeners.forEach((fn) => {
      try {
        fn(state);
      } catch (e) {
        /* one bad listener must not stop the others */
      }
    });
  };
}

/** Subscribes to scanner state changes; returns the unsubscribe function. */
export function subscribeScanState(fn) {
  install();
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Tests only: forget the install so a fresh window can be set up. */
export function resetScanTriggerForTests() {
  listeners.clear();
  installed = false;
}
